import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchTemplates, fetchTemplate, createTemplate, updateTemplate, deleteTemplate, blankTemplate,
} from '../services/reviewTemplateService';
import type { ReviewTemplate, OpticsCriteria, CharacterCriteria } from '../types';

// ─── Small sub-components ─────────────────────────────────────────────────────

const RubricEditor: React.FC<{
  rubric: Record<number, string>;
  onChange: (r: Record<number, string>) => void;
}> = ({ rubric, onChange }) => (
  <div className="grid gap-1.5 mt-2">
    {[1, 2, 3, 4, 5].map(n => (
      <div key={n} className="flex items-start gap-2">
        <span className="text-xs text-gray-500 mt-2 w-4 shrink-0">{n}</span>
        <input
          value={rubric[n] ?? ''}
          onChange={e => onChange({ ...rubric, [n]: e.target.value })}
          placeholder={`Score ${n} description`}
          className="flex-1 bg-white text-gray-900 text-xs border border-gray-300 rounded px-2 py-1.5 focus:border-blue-500 focus:outline-none"
        />
      </div>
    ))}
  </div>
);

const QuestionsEditor: React.FC<{
  questions: string[];
  onChange: (q: string[]) => void;
}> = ({ questions, onChange }) => (
  <div className="space-y-1.5 mt-2">
    {questions.map((q, i) => (
      <div key={i} className="flex gap-2">
        <input
          value={q}
          onChange={e => {
            const updated = [...questions];
            updated[i] = e.target.value;
            onChange(updated);
          }}
          placeholder={`Question ${i + 1}`}
          className="flex-1 bg-white text-gray-900 text-xs border border-gray-300 rounded px-2 py-1.5 focus:border-blue-500 focus:outline-none"
        />
        <button
          onClick={() => onChange(questions.filter((_, j) => j !== i))}
          className="text-brand-error text-xs px-2 hover:opacity-80"
        >
          ✕
        </button>
      </div>
    ))}
    <button
      onClick={() => onChange([...questions, ''])}
      className="text-brand-accent text-xs hover:underline mt-1"
    >
      + Add question
    </button>
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────

const TemplateBuilder: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);
  const [editing, setEditing] = useState<Omit<ReviewTemplate, 'id' | 'createdAt' | 'updatedAt'> | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [openCriteria, setOpenCriteria] = useState<string | null>(null);

  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    if (id) loadForEdit(id);
  }, [id]);

  const loadList = async () => {
    const list = await fetchTemplates();
    setTemplates(list);
  };

  const loadForEdit = async (tid: string) => {
    const t = await fetchTemplate(tid);
    if (t) {
      const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = t;
      setEditing(rest);
      setEditId(tid);
    }
  };

  const startNew = () => {
    setEditing(blankTemplate(user!.id));
    setEditId(null);
    navigate('/templates/new');
  };

  const handleSave = async () => {
    if (!editing || !editing.name.trim()) {
      setError('Template name is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (editId) {
        await updateTemplate(editId, editing);
      } else {
        await createTemplate(editing);
      }
      await loadList();
      setEditing(null);
      setEditId(null);
      navigate('/templates');
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (tid: string) => {
    if (!confirm('Delete this template?')) return;
    try {
      await deleteTemplate(tid);
      await loadList();
    } catch (e) {
      alert('Failed to delete template: ' + String(e));
    }
  };

  const removeOptics = (idx: number) => {
    if (!editing) return;
    setEditing({ ...editing, opticsCriteria: editing.opticsCriteria.filter((_, i) => i !== idx) });
    setOpenCriteria(null);
  };

  const removeCharacter = (idx: number) => {
    if (!editing) return;
    setEditing({ ...editing, characterCriteria: editing.characterCriteria.filter((_, i) => i !== idx) });
    setOpenCriteria(null);
  };

  const updateOptics = (idx: number, updated: Partial<OpticsCriteria>) => {
    if (!editing) return;
    const criteria = [...editing.opticsCriteria];
    criteria[idx] = { ...criteria[idx], ...updated };
    setEditing({ ...editing, opticsCriteria: criteria });
  };

  const updateCharacter = (idx: number, updated: Partial<CharacterCriteria>) => {
    if (!editing) return;
    const criteria = [...editing.characterCriteria];
    criteria[idx] = { ...criteria[idx], ...updated };
    setEditing({ ...editing, characterCriteria: criteria });
  };

  // ─── List view ──────────────────────────────────────────────────────────────

  if (!editing) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Review Templates</h1>
          <button
            onClick={startNew}
            className="bg-brand-accent hover:bg-blue-500 text-gray-900 text-sm font-semibold px-4 py-2 rounded-lg transition"
          >
            + New Template
          </button>
        </div>

        {templates.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-10">No templates yet.</p>
        ) : (
          <div className="space-y-3">
            {templates.map(t => (
              <div key={t.id} className="bg-white border border-gray-200 shadow-sm rounded-xl p-4 flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Scale: {t.scoringScale} · OPTICS: {Math.round(t.weightings.optics * 100)}% ·
                    Character: {Math.round(t.weightings.character * 100)}% ·
                    OKR+Ind: {Math.round(t.weightings.okrIndividual * 100)}%
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {t.opticsCriteria.length} OPTICS criteria ·{' '}
                    {t.characterCriteria.length} Character criteria ·
                    Max discretion ±{t.maxDiscretionDelta}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => { setEditing({ ...t }); setEditId(t.id); navigate(`/templates/${t.id}`); }}
                    className="text-xs text-brand-accent hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="text-xs text-brand-error hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Edit view ──────────────────────────────────────────────────────────────

  const totalOpticsWeight = editing.opticsCriteria.reduce((s, c) => s + c.weight, 0);
  const totalCharWeight = editing.characterCriteria.reduce((s, c) => s + c.weight, 0);
  const weightSum = editing.weightings.okrIndividual + editing.weightings.optics + editing.weightings.character;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {editId ? 'Edit Template' : 'New Template'}
        </h1>
        <button
          onClick={() => { setEditing(null); setEditId(null); navigate('/templates'); }}
          className="text-gray-500 hover:text-gray-900 text-sm"
        >
          ← Back
        </button>
      </div>

      {/* Basic info */}
      <Section title="Basic Info">
        <Field label="Template Name">
          <input
            value={editing.name}
            onChange={e => setEditing({ ...editing, name: e.target.value })}
            placeholder="e.g. Standard EPR Template 2025"
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Scoring Scale">
            <select
              value={editing.scoringScale}
              onChange={e => setEditing({ ...editing, scoringScale: Number(e.target.value) })}
              className={inputCls}
            >
              {[5, 10].map(n => <option key={n} value={n}>{n}-point</option>)}
            </select>
          </Field>
          <Field label="Max Discretion Delta">
            <input
              type="number"
              step="0.1"
              value={editing.maxDiscretionDelta}
              onChange={e => setEditing({ ...editing, maxDiscretionDelta: Number(e.target.value) })}
              className={inputCls}
            />
          </Field>
        </div>
      </Section>

      {/* Section weightings */}
      <Section title={`Section Weightings (total: ${Math.round(weightSum * 100)}% — should be 100%)`}>
        <div className="grid grid-cols-3 gap-4">
          {(
            [
              ['okrIndividual', 'OKR + Individual'],
              ['optics', 'OPTICS'],
              ['character', 'Character'],
            ] as const
          ).map(([key, label]) => (
            <Field key={key} label={`${label} (%)`}>
              <input
                type="number"
                step="5"
                min="0"
                max="100"
                value={Math.round(editing.weightings[key] * 100)}
                onChange={e =>
                  setEditing({
                    ...editing,
                    weightings: { ...editing.weightings, [key]: Number(e.target.value) / 100 },
                  })
                }
                className={inputCls}
              />
            </Field>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          OKR blend: Individual {Math.round(editing.teamVsIndividualSplit.individual * 100)}% ·
          Team {Math.round(editing.teamVsIndividualSplit.team * 100)}%
        </p>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <Field label="Individual split (%)">
            <input
              type="number"
              step="10"
              value={Math.round(editing.teamVsIndividualSplit.individual * 100)}
              onChange={e =>
                setEditing({
                  ...editing,
                  teamVsIndividualSplit: { individual: Number(e.target.value) / 100, team: 1 - Number(e.target.value) / 100 },
                })
              }
              className={inputCls}
            />
          </Field>
          <Field label="Team split (%)">
            <input
              type="number"
              step="10"
              value={Math.round(editing.teamVsIndividualSplit.team * 100)}
              readOnly
              className={`${inputCls} opacity-60`}
            />
          </Field>
        </div>
      </Section>

      {/* OPTICS Criteria */}
      <Section title={`OPTICS Criteria (total weight: ${Math.round(totalOpticsWeight * 100)}%)`}>
        {editing.opticsCriteria.map((c, idx) => (
          <div
            key={idx}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setOpenCriteria(openCriteria === `optics-${idx}` ? null : `optics-${idx}`)}
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 text-left"
            >
              <span className="text-sm font-medium text-gray-900">{c.name}</span>
              <span className="text-xs text-gray-500">{Math.round(c.weight * 100)}% {openCriteria === `optics-${idx}` ? '▲' : '▼'}</span>
            </button>
            {openCriteria === `optics-${idx}` && (
              <div className="p-3 space-y-3 bg-white/50">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name">
                    <input value={c.name} onChange={e => updateOptics(idx, { name: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Weight (%)">
                    <input type="number" step="0.5" value={Math.round(c.weight * 1000) / 10} onChange={e => updateOptics(idx, { weight: Number(e.target.value) / 100 })} className={inputCls} />
                  </Field>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Questions</p>
                  <QuestionsEditor questions={c.questions} onChange={q => updateOptics(idx, { questions: q })} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Rubric</p>
                  <RubricEditor rubric={c.rubric} onChange={r => updateOptics(idx, { rubric: r })} />
                </div>
                <div className="pt-2 border-t border-gray-100 flex justify-end">
                  <button
                    type="button"
                    onClick={() => { if (confirm(`Remove "${c.name}" from OPTICS criteria?`)) removeOptics(idx); }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    🗑 Remove this criteria
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        <button
          onClick={() =>
            setEditing({
              ...editing,
              opticsCriteria: [...editing.opticsCriteria, { name: 'New Criteria', weight: 0.025, questions: [], rubric: { 1: '', 2: '', 3: '', 4: '', 5: '' } }],
            })
          }
          className="text-brand-accent text-sm hover:underline mt-2"
        >
          + Add OPTICS Criteria
        </button>
      </Section>

      {/* Character Criteria */}
      <Section title={`Character Criteria (total: ${Math.round(totalCharWeight * 100)}%)`}>
        {editing.characterCriteria.map((c, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenCriteria(openCriteria === `char-${idx}` ? null : `char-${idx}`)}
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 text-left"
            >
              <span className="text-sm font-medium text-gray-900">{c.name}</span>
              <span className="text-xs text-gray-500">{Math.round(c.weight * 100)}% {openCriteria === `char-${idx}` ? '▲' : '▼'}</span>
            </button>
            {openCriteria === `char-${idx}` && (
              <div className="p-3 space-y-3 bg-white/50">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name">
                    <input value={c.name} onChange={e => updateCharacter(idx, { name: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Weight (%)">
                    <input type="number" step="5" value={Math.round(c.weight * 100)} onChange={e => updateCharacter(idx, { weight: Number(e.target.value) / 100 })} className={inputCls} />
                  </Field>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Rubric</p>
                  <RubricEditor rubric={c.rubric} onChange={r => updateCharacter(idx, { rubric: r })} />
                </div>
                <div className="pt-2 border-t border-gray-100 flex justify-end">
                  <button
                    type="button"
                    onClick={() => { if (confirm(`Remove "${c.name}" from Character criteria?`)) removeCharacter(idx); }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    🗑 Remove this criteria
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        <button
          onClick={() =>
            setEditing({
              ...editing,
              characterCriteria: [...editing.characterCriteria, { name: 'New Criteria', weight: 0.10, rubric: { 1: '', 2: '', 3: '', 4: '', 5: '' } }],
            })
          }
          className="text-brand-accent text-sm hover:underline mt-2"
        >
          + Add Character Criteria
        </button>
      </Section>

      {error && <p className="text-brand-error text-sm">{error}</p>}

      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={busy}
          className="bg-brand-accent hover:bg-blue-500 text-gray-900 font-semibold px-6 py-2 rounded-lg text-sm transition disabled:opacity-50"
        >
          {busy ? 'Saving…' : editId ? 'Update Template' : 'Create Template'}
        </button>
        <button
          onClick={() => { setEditing(null); navigate('/templates'); }}
          className="text-gray-500 hover:text-gray-900 text-sm px-4 py-2"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Layout helpers ───────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5 space-y-4">
    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h2>
    {children}
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="text-xs text-gray-500 font-medium block mb-1">{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full bg-white text-gray-900 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:border-blue-500 focus:outline-none';

export default TemplateBuilder;
